import React, { useEffect, useState } from "react";
import { Button, Form, Input, Spin, Tooltip } from "antd";
import { LoadingOutlined, PlusOutlined, SyncOutlined } from "@ant-design/icons";
import UpdateCaptureKeyModal from "./Modals/UpdateCaptureKeyModal";
import { createNotification } from "../Notification";
import { listProductionKeys } from "../../services/account/api";
import { useAuth } from "../../contexts/auth";
import UpdateWebhookKeyModal from "./Modals/UpdateWebhookKey";
import UpdateRestApiKeyModal from "./Modals/UpdateRestApiKey";
import { FormattedMessage, useIntl } from "react-intl";
import { updateProductionPartnerKey } from "../../services/credentials/api";
import { editWorkspace } from "../../services/workspace/api";
import { HasPermission } from "@/helpers/permission";

function ProductionForm() {
  const intl = useIntl();

  const { currentWorkspace, workspaces, refreshUser } = useAuth();

  const [data, setData] = useState();
  const [isLoading, setLoading] = useState(true);

  const [isUpdateCaptureKeyModalOpen, setIsUpdateCaptureKeyModalOpen] =
    useState(false);

  const [isUpdateWebhookKeyModalOpen, setIsUpdateWebhookKeyModalOpen] =
    useState(false);

  const [isUpdateRestApiKeyModalOpen, setIsUpdateRestApiKeyModalOpen] =
    useState(false);

  const [updating, setUpdating] = useState({
    prod_webhook_url: null,
    capture: null,
    webhook: null,
    rest_api: null,
  });

  useEffect(() => {
    loadProductionKeys();
  }, []);

  const loadProductionKeys = async () => {
    await listProductionKeys(currentWorkspace)
      .then((res) => {
        setData(res);
        setLoading(false);
      })
      .catch((error) => {
        console.error(error);
        createNotification("error", "Error", error.response.data.message);
      });
  };

  const handleBlur = async (e) => {
    let payload = {};
    let temp = updating;
    const field = e.target.id;
    if (updating[field] === true) {
      temp[field] = "validating";
      setUpdating({ ...updating, ...temp });
      payload[field] = e.target.value;

      await editWorkspace(currentWorkspace, payload)
        .then(() => {
          temp[field] = "success";
          setUpdating({ ...updating, ...temp });
          refreshUser();
        })
        .catch(() => {
          temp[field] = "error";
          setUpdating({ ...updating, ...temp });
        });
    }
  };

  const generateKey = async (type, date) => {
    setUpdating({ ...updating, [type]: "loading" });
    await updateProductionPartnerKey(currentWorkspace, { type, date }).then(() => {
      createNotification(
        "success",
        intl.formatMessage({
          id: "page.integration.capture.modal.success.title",
        }),
        intl.formatMessage({
          id: "page.integration.capture.modal.production.success.description",
        }),
      );
      loadProductionKeys();
      setIsUpdateRestApiKeyModalOpen(false);
      setIsUpdateCaptureKeyModalOpen(false);
      setIsUpdateWebhookKeyModalOpen(false);
    });
  };

  const reloadKey = async (key) => {
    if (key === "capture_key") {
      setIsUpdateCaptureKeyModalOpen(true);
    }
    if (key === "restapi_key") {
      setIsUpdateRestApiKeyModalOpen(true);
    }
    if (key === "webhook_key") {
      setIsUpdateWebhookKeyModalOpen(true);
    }
  };

  function getCurrentWorkspace() {
    return workspaces.find((workspace) => workspace.external_id === currentWorkspace);
  }

  const renderMessage = (status) => {
    if (updating[status] === "validating") return "Data is being saved...";
    if (updating[status] === "error")
      return "Data can't be saved. Try again later...";
    if (updating[status] === "success") return "Data was updated successfully.";
  };

  if (isLoading) {
    return (
      <div className="py-5 px-7 min-h-[418px] rounded-xl flex justify-center items-center bg-white border-1 shadow-sm">
        <Spin size="large" indicator={<LoadingOutlined spin />} />
      </div>
    );
  }

  return (
    <div className="py-5 px-7 rounded-xl bg-white border-1 shadow-sm">
      <Form layout="vertical">
        <Form.Item
          label={intl.formatMessage({
            id: "page.integration.capture-key",
          })}
        >
          {(!data.capture_key && (
            <Button
              disabled={updating.capture === "loading"}
              onClick={() => generateKey("capture")}
              size="large"
              type="default"
              className="w-full flex justify-center items-center text-center"
            >
              {updating.capture === "loading" && (
                <LoadingOutlined spin className="w-4 h-4 mr-1" />
              )}
              {updating.capture === null && <PlusOutlined className="mr-1" />}
              <FormattedMessage id="page.integration.capture.generate.button" />
            </Button>
          )) || (
              <Input
                size="large"
                readOnly
                value={data.capture_key}
                addonAfter={
                  HasPermission("refresh_production_credentials") ? (
                    <Tooltip title="Refresh">
                      <SyncOutlined onClick={() => reloadKey("capture_key")} />
                    </Tooltip>
                  ) : null
                }
              />
            )}
        </Form.Item>
        <Form.Item
          label="Webhook URL:"
          hasFeedback
          validateStatus={updating.prod_webhook_url}
          help={renderMessage("prod_webhook_url")}
          rules={[
            {
              required: true,
              message: "Please input your sandbox webhook URL!",
            },
          ]}
        >
          <Input
            id="prod_webhook_url"
            size="large"
            addonBefore="https://"
            defaultValue={getCurrentWorkspace().prod_webhook_url}
            readOnly={!HasPermission("refresh_production_credentials")}
            onBlur={(e) => handleBlur(e)}
            onInput={(e) => {
              setUpdating({ ...updating, prod_webhook_url: true });
            }}
            type="text"
            placeholder="Set the webhook URL to receive the transactions."
          />
        </Form.Item>
        <Form.Item
          label={intl.formatMessage({
            id: "page.integration.webhook-key",
          })}
        >
          {(!data.webhook_key && (
            <Button
              disabled={updating.webhook === "loading"}
              onClick={() => generateKey("webhook")}
              size="large"
              type="default"
              className="w-full flex justify-center items-center text-center"
            >
              {updating.webhook === "loading" && (
                <LoadingOutlined spin className="w-4 h-4 mr-1" />
              )}
              {updating.webhook === null && <PlusOutlined className="mr-1" />}
              <FormattedMessage id="page.integration.capture.generate.button" />
            </Button>
          )) || (
              <Input
                size="large"
                readOnly
                value={data.webhook_key}
                addonAfter={
                  HasPermission("refresh_production_credentials") ? (
                    <Tooltip title="Refresh">
                      <SyncOutlined onClick={() => reloadKey("webhook_key")} />
                    </Tooltip>
                  ) : null
                }
              />
            )}
        </Form.Item>
        <Form.Item
          label={intl.formatMessage({
            id: "page.integration.restapi-key",
          })}
        >
          {(!data.restapi_key && (
            <Button
              disabled={updating.rest_api === "loading"}
              onClick={() => generateKey("restapi")}
              size="large"
              type="default"
              className="w-full flex justify-center items-center text-center"
            >
              {updating.rest_api === "loading" && (
                <LoadingOutlined spin className="w-4 h-4 mr-1" />
              )}
              {updating.rest_api === null && <PlusOutlined className="mr-1" />}
              <FormattedMessage id="page.integration.capture.generate.button" />
            </Button>
          )) || (
              <Input
                size="large"
                readOnly
                value={data.restapi_key}
                addonAfter={
                  HasPermission("refresh_production_credentials") ? (
                    <Tooltip title="Refresh">
                      <SyncOutlined onClick={() => reloadKey("restapi_key")} />
                    </Tooltip>
                  ) : null
                }
              />
            )}
        </Form.Item>
      </Form>
      <UpdateCaptureKeyModal
        open={isUpdateCaptureKeyModalOpen}
        onCancel={() => setIsUpdateCaptureKeyModalOpen(false)}
        onSubmit={(date) => {
          generateKey("capture", date);
        }}
        environment={"production"}
        environment_label={intl
          .formatMessage({
            id: "page.myAccount.environments.production.title",
          })
          .toLocaleLowerCase()}
      />
      <UpdateWebhookKeyModal
        open={isUpdateWebhookKeyModalOpen}
        onCancel={() => setIsUpdateWebhookKeyModalOpen(false)}
        onSubmit={(date) => {
          generateKey("webhook", date);
        }}
        environment={"production"}
        environment_label={intl
          .formatMessage({
            id: "page.myAccount.environments.production.title",
          })
          .toLocaleLowerCase()}
      />
      <UpdateRestApiKeyModal
        open={isUpdateRestApiKeyModalOpen}
        onCancel={() => setIsUpdateRestApiKeyModalOpen(false)}
        onSubmit={(date) => {
          generateKey("restapi", date);
        }}
        environment={"production"}
        environment_label={intl
          .formatMessage({
            id: "page.myAccount.environments.production.title",
          })
          .toLocaleLowerCase()}
      />
    </div>
  );
}
export default ProductionForm;
